import { takeLatest, put, call } from 'redux-saga/effects';
import { IDomain } from '../../types';
import {
  GET_DOMAINS_REQUEST,
  CREATE_DOMAIN_REQUEST,
  GET_DOMAIN_REQUEST,
  VERIFY_DOMAIN_REQUEST,
  DELETE_DOMAIN_REQUEST,
  CreateDomainRequestAction,
  GetDomainRequestAction,
  VerifyDomainRequestAction,
  DeleteDomainRequestAction,
} from './domain.types';
import {
  getDomainsSuccess,
  getDomainsFailure,
  createDomainSuccess,
  createDomainFailure,
  getDomainSuccess,
  getDomainFailure,
  getDomainsRequest,
  verifyDomainSuccess,
  verifyDomainFailure,
  getDomainRequest,
  deleteDomainSuccess,
  deleteDomainFailure,
} from './domain.action';
import { getDomainService } from '../../services/domainService';

interface DomainResponse {
  domains: IDomain[];
  domain: IDomain;
}

// API calls using services
const getDomains = async () => {
  // Use AWS service as default for common endpoints
  const service = getDomainService('aws');
  return service.getDomains();
};

const getDomain = async (domainName: string) => {
  // Use AWS service as default for common endpoints
  const service = getDomainService('aws');
  return service.getDomain(domainName);
};

const createDomain = async (
  payload: Partial<IDomain>,
  provider: 'aws' | 'mailgun'
) => {
  const service = getDomainService(provider);
  return service.createDomain(payload);
};

const verifyDomain = async (domainId: string, provider: 'aws' | 'mailgun') => {
  const service = getDomainService(provider);
  return service.verifyDomain(domainId);
};

const deleteDomain = async (
  domainName: string,
  provider: 'aws' | 'mailgun'
) => {
  const service = getDomainService(provider);
  return service.deleteDomain(domainName);
};

// Saga workers
function* getDomainsWorker() {
  try {
    const response: DomainResponse = yield call(getDomains);
    yield put(getDomainsSuccess(response.domains));
  } catch (error: any) {
    yield put(
      getDomainsFailure(error.response?.data || 'Failed to fetch domains')
    );
  }
}

function* getDomainWorker({ payload: domainName }: GetDomainRequestAction) {
  try {
    const response: DomainResponse = yield call(getDomain, domainName);
    yield put(getDomainSuccess(response.domain));
    yield put(verifyDomainSuccess(response.domain));
  } catch (error: any) {
    yield put(
      getDomainFailure(error.response?.data || 'Failed to fetch domain')
    );
  }
}

function* createDomainWorker({ payload }: CreateDomainRequestAction) {
  try {
    const { domainData, provider } = payload;
    const response: IDomain = yield call(createDomain, domainData, provider);
    yield put(createDomainSuccess(response));
    // After successful domain creation, fetch all domains
    yield put(getDomainsRequest());
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details ||
      'Failed to create domain';
    yield put(createDomainFailure(errorMessage));
  }
}

function* verifyDomainWorker({ payload }: VerifyDomainRequestAction) {
  const { domain, source, provider } = payload;
  try {
    const response: IDomain = yield call(verifyDomain, domain, provider);
    yield put(verifyDomainSuccess(response));

    // Call appropriate request based on source
    if (source === 'domainRecords') {
      yield put(getDomainRequest(domain));
    } else {
      yield put(getDomainsRequest());
    }
  } catch (error: any) {
    console.log('error', error);
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      error.response?.data?.message?.details ||
      'Failed to verify domain';
    yield put(verifyDomainFailure(errorMessage));
    // Refetch domain(s) even on error so UI has latest state
    if (source === 'domainRecords') {
      yield put(getDomainRequest(domain));
    } else {
      yield put(getDomainsRequest());
    }
  }
}

function* deleteDomainWorker({ payload }: DeleteDomainRequestAction) {
  try {
    const { domainName, provider } = payload;
    yield call(deleteDomain, domainName, provider);
    yield put(deleteDomainSuccess(domainName));
    // After successful deletion, fetch all domains
    yield put(getDomainsRequest());
  } catch (error: any) {
    yield put(
      deleteDomainFailure(
        error.response?.data?.message || 'Failed to delete domain'
      )
    );
  }
}

// Saga watchers
export function* domainSaga() {
  yield takeLatest(GET_DOMAINS_REQUEST, getDomainsWorker);
  yield takeLatest(GET_DOMAIN_REQUEST, getDomainWorker);
  yield takeLatest(CREATE_DOMAIN_REQUEST, createDomainWorker);
  yield takeLatest(VERIFY_DOMAIN_REQUEST, verifyDomainWorker);
  yield takeLatest(DELETE_DOMAIN_REQUEST, deleteDomainWorker);
}
